import { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { CookieManager } from '../../../../identityaccess/infrastructure/storage/CookieManager';
import { AUTH_CONFIG } from '../../../../../core/config/auth.config';

/**
 * RefreshTokenInterceptor
 * Handles 401 errors by attempting to refresh the access token
 */
export class RefreshTokenInterceptor {
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  constructor(
    private axiosInstance: AxiosInstance,
    private refreshTokenEndpoint: string,
    private onTokenRefreshed: (accessToken: string) => void,
    private onRefreshFailed: () => void,
  ) {}

  /**
   * Register the interceptor
   */
  public register(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // If error is 401 and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Skip refresh for login and refresh endpoints
          if (
            originalRequest.url?.includes('/login') ||
            originalRequest.url?.includes(this.refreshTokenEndpoint)
          ) {
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // Queue the request while refresh is in progress
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.axiosInstance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // No refresh token cookie — skip the attempt and fail immediately
            if (!CookieManager.hasCookie(AUTH_CONFIG.refreshTokenFlagCookie)) {
              this.processQueue(new Error('No refresh token'));
              this.onRefreshFailed();
              return Promise.reject(error);
            }

            // Attempt to refresh the token
            const response = await this.axiosInstance.post<{
              access_token: string;
              token_type: string;
              expires_in: number;
            }>(this.refreshTokenEndpoint);

            const newAccessToken = response.data.access_token;
            this.onTokenRefreshed(newAccessToken);

            // Process queued requests
            this.processQueue(null);

            // Retry the original request
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh failed - clear queue and notify
            this.processQueue(refreshError);
            this.onRefreshFailed();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Process all queued requests
   */
  private processQueue(error: unknown): void {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });

    this.failedQueue = [];
  }
}
